import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  Mail,
  MessageSquareText,
  Phone,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { IronviewLogo } from '@/shared/components/IronviewLogo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form/form';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { CONTACT_INFO } from '@/shared/components/home/constants';

const supportRequestSchema = z.object({
  name: z.string().trim().min(2, 'Enter your full name').max(200),
  email: z.string().trim().email('Enter a valid email address').max(254),
  phone: z.string().trim().max(50),
  subject: z.string().trim().min(3, 'Tell us what you need help with').max(255),
  message: z
    .string()
    .trim()
    .min(10, 'Please include a little more detail')
    .max(5000, 'Keep your message under 5,000 characters'),
});

type SupportRequestValues = z.infer<typeof supportRequestSchema>;

type SupportResponse = {
  detail?: string;
  message?: string;
  reference?: string;
  success?: boolean;
  [key: string]: unknown;
};

const responseErrorMessage = (payload: SupportResponse) => {
  if (typeof payload.detail === 'string') return payload.detail;
  if (typeof payload.message === 'string') return payload.message;
  const firstFieldError = Object.values(payload).find(
    (value): value is string[] => Array.isArray(value) && typeof value[0] === 'string',
  );
  return firstFieldError?.[0] ?? 'We could not send your request. Please try again.';
};

const PublicSupportPage = () => {
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const form = useForm<SupportRequestValues>({
    resolver: zodResolver(supportRequestSchema),
    defaultValues: {
      email: '',
      message: '',
      name: '',
      phone: '',
      subject: '',
    },
  });

  useEffect(() => {
    document.title = 'Support | Ironview';
  }, []);

  const onSubmit = async (values: SupportRequestValues) => {
    form.clearErrors('root');
    try {
      const response = await fetch('/api/v1/support/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          source: 'app_store_support',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as SupportResponse;
      if (!response.ok || !payload.success) {
        throw new Error(responseErrorMessage(payload));
      }
      form.reset();
      setConfirmation(payload.reference ?? 'received');
    } catch (error) {
      form.setError('root', {
        message:
          error instanceof Error
            ? error.message
            : 'We could not send your request. Please try again.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-maintenance-dark text-white">
      <header className="border-b border-white/10 bg-maintenance-navy/95">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-4 sm:px-8">
          <a href="/" aria-label="Ironview home" className="inline-flex items-center">
            <IronviewLogo tone="light" className="w-48 sm:w-56" />
          </a>
          <a
            href="/"
            className="inline-flex min-h-11 items-center gap-2 rounded-[10px] border border-white/20 px-4 text-sm font-semibold text-white/80 transition hover:border-white/50 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Ironview
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,104,12,0.2),transparent_42%)]" />
          <div className="relative mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-primary">
                <MessageSquareText className="h-4 w-4" />
                Ironview Support
              </div>
              <h1 className="text-4xl font-black tracking-tight sm:text-5xl md:text-6xl">
                How can we help?
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/65 sm:text-lg">
                Tell us what is getting in your way. Our team will review your request and
                follow up using the email address you provide.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-8 px-5 py-10 sm:px-8 sm:py-14 lg:grid-cols-[0.72fr_1.28fr]">
          <aside className="space-y-5">
            <Card className="border-white/10 bg-maintenance-slate/70 text-white">
              <CardHeader>
                <CardTitle>Contact support</CardTitle>
                <CardDescription className="text-white/55">
                  Reach the Ironview team directly.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <a
                  href={'mailto:' + CONTACT_INFO.email}
                  className="flex min-h-14 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 transition hover:border-primary/50 hover:bg-white/[0.07]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-white/45">
                      Email
                    </span>
                    <span className="font-semibold text-white">{CONTACT_INFO.email}</span>
                  </span>
                </a>
                <a
                  href={'tel:' + CONTACT_INFO.phone.replace(/\./g, '')}
                  className="flex min-h-14 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 transition hover:border-primary/50 hover:bg-white/[0.07]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <Phone className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-white/45">
                      Phone
                    </span>
                    <span className="font-semibold text-white">{CONTACT_INFO.phone}</span>
                  </span>
                </a>
              </CardContent>
            </Card>

            <div className="rounded-[1.375rem] border border-white/10 bg-white/[0.035] p-6">
              <LockKeyhole className="h-5 w-5 text-primary" />
              <h2 className="mt-4 text-base font-bold">Account access</h2>
              <p className="mt-2 text-sm leading-6 text-white/55">
                Already have an Ironview account? You can also sign in and use the Support area
                to submit and track bug reports.
              </p>
              <a
                href="/login"
                className="mt-4 inline-flex min-h-11 items-center font-semibold text-primary hover:brightness-110"
              >
                Sign in to Ironview
              </a>
            </div>
          </aside>

          <Card className="border-white/10 bg-white text-slate-950 shadow-2xl shadow-black/25">
            <CardHeader className="border-b border-slate-200">
              <CardTitle>Send a help request</CardTitle>
              <CardDescription>
                Required fields are marked with an asterisk.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {confirmation ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-8 w-8" />
                  </span>
                  <h2 className="mt-5 text-2xl font-black">Your request is on its way</h2>
                  <p className="mt-3 max-w-md leading-6 text-slate-600">
                    Thanks for contacting Ironview. Our support team will follow up by email.
                  </p>
                  {confirmation !== 'received' && (
                    <p className="mt-3 text-xs text-slate-400">
                      Reference: {confirmation}
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-7"
                    onClick={() => setConfirmation(null)}
                  >
                    Send another request
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    {form.formState.errors.root?.message && (
                      <Alert variant="destructive">
                        <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
                      </Alert>
                    )}

                    <div className="grid gap-5 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full name *</FormLabel>
                            <FormControl>
                              <Input autoComplete="name" placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email address *</FormLabel>
                            <FormControl>
                              <Input
                                autoComplete="email"
                                inputMode="email"
                                placeholder="you@company.com"
                                type="email"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone number</FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="tel"
                              inputMode="tel"
                              placeholder="Optional"
                              type="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>What do you need help with? *</FormLabel>
                          <FormControl>
                            <Input placeholder="A short summary" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Details *</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-[150px] resize-y"
                              placeholder="Describe what happened, what you expected, and which project or screen you were using."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full sm:w-auto"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending request
                        </>
                      ) : (
                        'Send help request'
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </section>
      </main>

      <footer className="border-t border-white/10 px-5 py-6 text-center text-sm text-white/40">
        Ironview 360 LLC · {CONTACT_INFO.email} · {CONTACT_INFO.phone}
        <a href="/privacy" className="ml-2 font-semibold text-white/60 hover:text-white">
          · Privacy Policy
        </a>
        <span className="mt-1 block">{CONTACT_INFO.copyright}</span>
      </footer>
    </div>
  );
};

export default PublicSupportPage;
